import React from 'react'
import { Chart } from '../../legacy-design-system/charts/chart'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import * as Highcharts from 'highcharts'
import { I18nConsumer } from '../i18n-provider'
import { IntlShape } from 'react-intl'
import {
  buildYAxisPlotLinesOptions,
  buildChartSeriesOptions,
  getChartTooltipContainerCss,
  getChartPointBySeriesID,
  getChartTooltipHeaderTpl,
  getChartTooltipSeriesRowTpl,
  MARKER_RADIUS,
  setArrowForSharedTooltip,
  sharedTooltipPadding,
  ChartSeriesConfig,
  linearGradient,
  buildCrosshair
} from '../../legacy-design-system/charts/index'

export const routeCoverageChartTestId = 'route-coverage-chart-test-id'

export type RouteSnapshotPeriodType =
  | 'CURRENT'
  | 'SINCE_YESTERDAY'
  | 'SINCE_LAST_WEEK'

export type ChartBaseRouteStatusType = 'DISCOVERED' | 'EXERCISED' | 'VULNERABLE'

export type ChartRouteStatusType = ChartBaseRouteStatusType | 'NOT_EXERCISED'

export type RouteStatusSeriesType = ChartRouteStatusType | 'HIDDEN_SCATTER'

export type RoutesFiltersType = 'ALL' | ChartRouteStatusType
export type RouteSnapshotBreakdown = Record<RouteSnapshotPeriodType, number[]>
export type RoutesBreakdown = Record<
  ChartRouteStatusType,
  RouteSnapshotBreakdown
>
export type MostRecentRoutesBreakdown = Record<ChartRouteStatusType, number>

const TOP_SERIES_Z_INDEX = 3

const CHART_SERIES: ChartSeriesConfig<RouteStatusSeriesType> = {
  DISCOVERED: {
    zIndex: 1,
    id: 'DISCOVERED',
    lineColor: theme.color.neutral40,
    areaColor: {
      linearGradient,
      stops: [
        [0, deprecatedTheme.secondaryColors.chartDiscoveredAreaBottom],
        [1, deprecatedTheme.secondaryColors.chartDiscoveredAreaTop]
      ]
    },
    areaColorBg: deprecatedTheme.secondaryColors.chartDiscoveredAreaBg
  },
  EXERCISED: {
    zIndex: 2,
    id: 'EXERCISED',
    lineColor: theme.color.teal50,
    areaColor: {
      linearGradient,
      stops: [
        [0, deprecatedTheme.secondaryColors.chartExercisedAreaBottom],
        [1, deprecatedTheme.secondaryColors.chartExercisedAreaTop]
      ]
    },
    areaColorBg: deprecatedTheme.secondaryColors.chartExercisedAreaBg
  },
  VULNERABLE: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'VULNERABLE',
    lineColor: theme.color.red50,
    areaColor: {
      linearGradient,
      stops: [
        [0, deprecatedTheme.secondaryColors.chartVulnerableAreaBottom],
        [1, deprecatedTheme.secondaryColors.chartVulnerableAreaTop]
      ]
    },
    areaColorBg: deprecatedTheme.secondaryColors.chartVulnerableAreaBg
  },
  NOT_EXERCISED: {
    zIndex: 1,
    id: 'NOT_EXERCISED',
    lineColor: theme.color.neutral40,
    areaColor: {
      linearGradient,
      stops: [
        [0, deprecatedTheme.secondaryColors.chartDiscoveredAreaBottom],
        [1, deprecatedTheme.secondaryColors.chartDiscoveredAreaTop]
      ]
    },
    areaColorBg: deprecatedTheme.secondaryColors.chartDiscoveredAreaBg
  },
  HIDDEN_SCATTER: {
    zIndex: 0,
    id: 'HIDDEN_SCATTER',
    lineColor: theme.color.transparent,
    areaColor: theme.color.transparent,
    areaColorBg: theme.color.transparent
  }
}

interface Props {
  /**
   * chart height
   */
  readonly height: number
  /**
   * values used in order to fill the Y axis data points and tooltips.
   */
  readonly routesBreakdown: RoutesBreakdown
  /**
   * values used in order to fill the X axis data points.
   */
  readonly timeline: number[]
  /**
   * initial filter selected which controls just the first chart visualization.
   */
  readonly initialFilter: RoutesFiltersType
  /**
   * values used in order to draw the Y Axis dotted plot lines (values for today snapshot).
   */
  readonly mostRecentRoutesBreakdown: MostRecentRoutesBreakdown
}

interface State {
  readonly filter: RoutesFiltersType
  readonly animationEnabled: boolean
}

export class RouteCoverageChart extends React.Component<Props, State> {
  public static getDerivedStateFromProps(props: Props) {
    return {
      filter: props.initialFilter
    }
  }

  public constructor(props) {
    super(props)
    setArrowForSharedTooltip()

    const { initialFilter } = props
    this.state = {
      filter: initialFilter,
      animationEnabled: true
    }
  }

  public shouldComponentUpdate(nextProps: Props) {
    const { initialFilter: currentFilter, timeline: currentTimeline } =
      this.props
    const { initialFilter: nextFilter, timeline: nextTimeline } = nextProps

    // Only re-render the chart if the selected filter or date range changes
    return (
      currentFilter !== nextFilter ||
      currentTimeline.length !== nextTimeline.length
    )
  }

  public render() {
    return (
      <I18nConsumer>
        {({ intl }) => (
          <Chart
            testId={routeCoverageChartTestId}
            options={this.buildChartOptions(intl)}
          />
        )}
      </I18nConsumer>
    )
  }

  private updateSelectedFilterState = (filter: RoutesFiltersType) => {
    this.setState({
      filter,
      animationEnabled: false // chart series animation should only happen in the initial render
    })
  }

  private isAllFilterSelected = (): boolean => {
    const { filter } = this.state
    return filter === 'ALL'
  }

  private buildChartOptions = (intl: IntlShape): Highcharts.Options => {
    const { height } = this.props
    const { filter, animationEnabled } = this.state

    const isAllFilterSelected = this.isAllFilterSelected()
    const renderIndividualTooltip = this.renderIndividualTooltip
    const renderSharedTooltip = this.renderSharedTooltip

    return {
      chart: {
        height: height.valueOf(),
        type: 'areaspline',
        plotBackgroundColor: 'transparent', // needs to be set in this way in order to get chart.plotBackground defined
        events: {
          click: () =>
            isAllFilterSelected
              ? undefined
              : this.updateSelectedFilterState('ALL'),
          load() {
            // @ts-ignore plotBackground is available in 8.2 for this workaround, but not in the TS definition
            this.plotBackground.htmlCss({
              cursor: isAllFilterSelected ? undefined : 'pointer'
            })
          }
        }
      },
      title: {
        text: undefined
      },
      tooltip: {
        shared: isAllFilterSelected,
        backgroundColor: theme.color.neutral90,
        borderRadius: 8,
        padding: 0,
        borderWidth: 0,
        shadow: true,
        shape: 'callout',
        useHTML: true,

        formatter() {
          if (isAllFilterSelected) {
            return renderSharedTooltip(this.points, this.x, intl)
          } else {
            return this.series.options.id === filter
              ? renderIndividualTooltip(this.y, this.x, intl)
              : false
          }
        }
      },
      plotOptions: {
        series: {
          animation: animationEnabled,
          marker: {
            enabled: false,
            fillColor: theme.color.white,
            symbol: 'circle',
            radius: MARKER_RADIUS
          }
        }
      },
      xAxis: [this.getXAxisOptions(isAllFilterSelected)],
      yAxis: {
        allowDecimals: false,
        tickAmount: 5,
        labels: {
          style: {
            color: theme.color.neutral80,
            fontWeight: deprecatedTheme.fontWeights.primary.medium.toString(),
            fontFamily: deprecatedTheme.fontFamilies.primaryCondensed,
            fontSize: '12'
          }
        },
        plotLines: this.getPlotLines()
      },
      series: isAllFilterSelected
        ? this.buildAllFilterSeriesOptions()
        : this.buildFilterSeriesOptions()
    }
  }

  private getXAxisOptions = (
    showCrosshair: boolean
  ): Highcharts.XAxisOptions => {
    const { timeline } = this.props
    const hoursInADay = 24
    const hoursInAMonth = hoursInADay * 31
    const isMoreThanOneMonthData = timeline.length > hoursInAMonth + 1
    const isLessOrEqualThanOneDayData = timeline.length <= hoursInADay + 1

    return {
      type: 'datetime',
      labels: {
        formatter() {
          return isLessOrEqualThanOneDayData
            ? Highcharts.dateFormat(
                '%b %e %H:%M',
                this.value as number
              ).toUpperCase()
            : Highcharts.dateFormat('%b %e', this.value as number).toUpperCase()
        },
        style: {
          color: theme.color.neutral90,
          fontSize: '13'
        }
      },

      tickPositions:
        isMoreThanOneMonthData || isLessOrEqualThanOneDayData
          ? undefined
          : timeline.filter((timestamp) => {
              return new Date(timestamp).getUTCHours() === 0
            }),
      crosshair: showCrosshair ? buildCrosshair(TOP_SERIES_Z_INDEX + 1) : false,

      lineColor: theme.color.neutral80,
      lineWidth: 0.5,
      tickLength: 0,
      tickInterval: isLessOrEqualThanOneDayData ? 4 * 3600 * 1000 : undefined
    }
  }

  private getPlotLines = (): Highcharts.YAxisPlotLinesOptions[] => {
    const { mostRecentRoutesBreakdown } = this.props
    const { filter } = this.state

    let plotLineValue

    switch (filter) {
      case 'VULNERABLE':
        plotLineValue = mostRecentRoutesBreakdown.VULNERABLE
        break
      case 'DISCOVERED':
        plotLineValue = mostRecentRoutesBreakdown.DISCOVERED
        break
      case 'NOT_EXERCISED':
        plotLineValue = mostRecentRoutesBreakdown.NOT_EXERCISED
        break
      case 'EXERCISED':
      case 'ALL':
      default:
        plotLineValue = mostRecentRoutesBreakdown.EXERCISED
    }

    return !!plotLineValue
      ? [buildYAxisPlotLinesOptions(plotLineValue, TOP_SERIES_Z_INDEX + 1)]
      : []
  }

  private buildAllFilterSeriesOptions = (): Highcharts.SeriesOptionsType[] => {
    const { routesBreakdown, timeline } = this.props

    return [
      buildChartSeriesOptions(
        routesBreakdown.VULNERABLE.CURRENT,
        timeline,
        CHART_SERIES.VULNERABLE.id,
        CHART_SERIES.VULNERABLE.lineColor,
        CHART_SERIES.VULNERABLE.areaColor,
        CHART_SERIES.VULNERABLE.zIndex,
        'pointer',
        true,
        true,
        () => this.updateSelectedFilterState('VULNERABLE')
      ),
      buildChartSeriesOptions(
        routesBreakdown.EXERCISED.CURRENT,
        timeline,
        CHART_SERIES.EXERCISED.id,
        CHART_SERIES.EXERCISED.lineColor,
        CHART_SERIES.EXERCISED.areaColor,
        CHART_SERIES.EXERCISED.zIndex,
        'pointer',
        true,
        false,
        () => this.updateSelectedFilterState('EXERCISED')
      ),
      buildChartSeriesOptions(
        routesBreakdown.DISCOVERED.CURRENT,
        timeline,
        CHART_SERIES.DISCOVERED.id,
        CHART_SERIES.DISCOVERED.lineColor,
        CHART_SERIES.DISCOVERED.areaColor,
        CHART_SERIES.DISCOVERED.zIndex,
        'pointer',
        true,
        false,
        () => this.updateSelectedFilterState('DISCOVERED')
      ),
      this.buildHiddenPlotLineChartSeries()
    ]
  }

  private buildFilterSeriesOptions = (): Highcharts.SeriesOptionsType[] => {
    const { timeline, routesBreakdown } = this.props
    const { filter } = this.state

    const isVulnerableFilter = filter === 'VULNERABLE'
    const isExercisedFilter = filter === 'EXERCISED'
    const isDiscoveredFilter = filter === 'DISCOVERED'
    const isNotExercisedFilter = filter === 'NOT_EXERCISED'

    const seriesOptions: Highcharts.SeriesOptionsType[] = [
      buildChartSeriesOptions(
        routesBreakdown.DISCOVERED.CURRENT,
        timeline,
        CHART_SERIES.DISCOVERED.id,
        CHART_SERIES.DISCOVERED.lineColor,
        isDiscoveredFilter
          ? CHART_SERIES.DISCOVERED.areaColor
          : CHART_SERIES.DISCOVERED.areaColorBg,
        isDiscoveredFilter
          ? TOP_SERIES_Z_INDEX + 1
          : CHART_SERIES.DISCOVERED.zIndex,
        isDiscoveredFilter ? undefined : 'pointer',
        isDiscoveredFilter,
        true,
        isDiscoveredFilter
          ? undefined
          : () => this.updateSelectedFilterState('ALL')
      ),
      this.buildHiddenPlotLineChartSeries()
    ]

    if (isNotExercisedFilter) {
      seriesOptions.push(
        buildChartSeriesOptions(
          routesBreakdown.NOT_EXERCISED.CURRENT,
          timeline,
          CHART_SERIES.NOT_EXERCISED.id,
          CHART_SERIES.NOT_EXERCISED.lineColor,
          isNotExercisedFilter
            ? CHART_SERIES.NOT_EXERCISED.areaColor
            : CHART_SERIES.NOT_EXERCISED.areaColorBg,
          isNotExercisedFilter
            ? TOP_SERIES_Z_INDEX + 1
            : CHART_SERIES.NOT_EXERCISED.zIndex,
          undefined,
          true,
          true,
          undefined
        )
      )
    } else {
      seriesOptions.push(
        buildChartSeriesOptions(
          routesBreakdown.VULNERABLE.CURRENT,
          timeline,
          CHART_SERIES.VULNERABLE.id,
          CHART_SERIES.VULNERABLE.lineColor,
          isVulnerableFilter
            ? CHART_SERIES.VULNERABLE.areaColor
            : CHART_SERIES.VULNERABLE.areaColorBg,
          isVulnerableFilter
            ? TOP_SERIES_Z_INDEX + 1
            : CHART_SERIES.VULNERABLE.zIndex,
          isVulnerableFilter ? undefined : 'pointer',
          isVulnerableFilter,
          true,
          isVulnerableFilter
            ? undefined
            : () => this.updateSelectedFilterState('ALL')
        )
      )
      seriesOptions.push(
        buildChartSeriesOptions(
          routesBreakdown.EXERCISED.CURRENT,
          timeline,
          CHART_SERIES.EXERCISED.id,
          CHART_SERIES.EXERCISED.lineColor,
          isExercisedFilter
            ? CHART_SERIES.EXERCISED.areaColor
            : CHART_SERIES.EXERCISED.areaColorBg,
          isExercisedFilter
            ? TOP_SERIES_Z_INDEX + 1
            : CHART_SERIES.EXERCISED.zIndex,
          isExercisedFilter ? undefined : 'pointer',
          isExercisedFilter,
          false,
          isExercisedFilter
            ? undefined
            : () => this.updateSelectedFilterState('ALL')
        )
      )
    }

    return seriesOptions
  }

  /**
   * Sadly, Highcharts does not take into consideration the plotline values in order to resize the chart if it is necessary.
   * So, we need to add an extra Series (an invisible point)
   * in order to adjust the Y Axis height and be sure that it is always gonna be visible the Plot Line (dotted line).
   * https://forum.highcharts.com/highcharts-usage/plotline-always-visible-t34848/
   * https://stackoverflow.com/questions/13084849/how-to-always-show-the-plotline-in-highcharts
   */
  private buildHiddenPlotLineChartSeries =
    (): Highcharts.SeriesScatterOptions => {
      const { timeline } = this.props
      const firstTimelineValue = timeline[0]

      return {
        type: 'scatter',
        id: CHART_SERIES.HIDDEN_SCATTER.id,
        zIndex: CHART_SERIES.HIDDEN_SCATTER.zIndex,
        enableMouseTracking: false,
        stickyTracking: false,
        marker: {
          enabled: false
        },
        data: [[firstTimelineValue, this.getChartPlotLineValue()]]
      }
    }

  private getChartPlotLineValue = () => {
    const {
      mostRecentRoutesBreakdown: {
        DISCOVERED,
        VULNERABLE,
        NOT_EXERCISED,
        EXERCISED
      }
    } = this.props
    const { filter } = this.state

    switch (filter) {
      case 'DISCOVERED':
        return DISCOVERED
      case 'VULNERABLE':
        return VULNERABLE
      case 'NOT_EXERCISED':
        return NOT_EXERCISED
      case 'EXERCISED':
      case 'ALL':
      default:
        return EXERCISED
    }
  }

  private renderIndividualTooltip = (
    value,
    timestamp: number,
    intl: IntlShape
  ) => {
    const { filter } = this.state
    const {
      routesBreakdown: { DISCOVERED, EXERCISED, VULNERABLE },
      timeline
    } = this.props

    const timelinePoint = timeline.indexOf(timestamp)

    let label
    let sinceYesterdayValue
    let sinceLastWeekValue

    switch (filter) {
      case 'DISCOVERED':
        label = intl.formatMessage(
          {
            id: 'detail_applicationCoverage_discoveredRoutesFull'
          },
          {
            routesNumber: value
          }
        )
        sinceYesterdayValue = DISCOVERED.SINCE_YESTERDAY[timelinePoint]
        sinceLastWeekValue = DISCOVERED.SINCE_LAST_WEEK[timelinePoint]
        break
      case 'EXERCISED':
        label = intl.formatMessage(
          {
            id: 'detail_applicationCoverage_exercisedRoutesFull'
          },
          {
            routesNumber: value
          }
        )
        sinceYesterdayValue = EXERCISED.SINCE_YESTERDAY[timelinePoint]
        sinceLastWeekValue = EXERCISED.SINCE_LAST_WEEK[timelinePoint]
        break
      case 'VULNERABLE':
        label = intl.formatMessage(
          {
            id: 'detail_applicationCoverage_vulnerableRoutes'
          },
          {
            routesNumber: value
          }
        )
        sinceYesterdayValue = VULNERABLE.SINCE_YESTERDAY[timelinePoint]
        sinceLastWeekValue = VULNERABLE.SINCE_LAST_WEEK[timelinePoint]
        break
      case 'NOT_EXERCISED':
        label = intl.formatMessage(
          {
            id: 'detail_applicationCoverage_nRoutes'
          },
          {
            count: value
          }
        )
        break
      default:
        label = ''
    }

    let tooltipTpl = getChartTooltipHeaderTpl(
      label,
      Highcharts.dateFormat('%b %e, %Y %H:%M', timestamp)
    )

    tooltipTpl += '<div style="margin-bottom: 16px"></div>'

    const isNotExercisedFilter = filter === 'NOT_EXERCISED'
    const { routesBreakdown } = this.props

    const routes = routesBreakdown[filter]

    if (isNotExercisedFilter) {
      tooltipTpl += intl.formatMessage({
        id: 'detail_applicationCoverage_notExercisedRoutes'
      })
    } else {
      if (routes.SINCE_YESTERDAY.length > 0) {
        const yesterdayLabel = intl.formatMessage({
          id: 'detail_applicationsCoverage_detectedRoutesSinceYesterday'
        })
        tooltipTpl += getIndividualTooltipRowTpl(
          sinceYesterdayValue,
          yesterdayLabel
        )
      }
      if (routes.SINCE_LAST_WEEK.length > 0) {
        const lastWeekLabel = intl.formatMessage({
          id: 'detail_applicationsCoverage_detectedRoutesSinceLastWeek'
        })

        tooltipTpl += getIndividualTooltipRowTpl(
          sinceLastWeekValue,
          lastWeekLabel
        )
      }
    }

    return (
      '<div style="' +
      getChartTooltipContainerCss(sharedTooltipPadding) +
      '">' +
      tooltipTpl +
      '</div>'
    )
  }

  private renderSharedTooltip = (
    points,
    timestamp: number,
    intl: IntlShape
  ) => {
    const seriesPoints: Record<ChartBaseRouteStatusType, any> = {
      DISCOVERED: getChartPointBySeriesID(CHART_SERIES.DISCOVERED.id, points),
      EXERCISED: getChartPointBySeriesID(CHART_SERIES.EXERCISED.id, points),
      VULNERABLE: getChartPointBySeriesID(CHART_SERIES.VULNERABLE.id, points)
    }

    const tooltipSeriesRowTplConfig: Array<{
      readonly lineColor: string
      readonly i18nMessageID: string
      readonly count: number
    }> = [
      {
        lineColor: seriesPoints.DISCOVERED.series.options.lineColor,
        i18nMessageID: 'detail_applicationCoverage_discoveredRoutes',
        count: seriesPoints.DISCOVERED.y
      },
      {
        lineColor: seriesPoints.EXERCISED.series.options.lineColor,
        i18nMessageID: 'detail_applicationCoverage_exercisedRoutes',
        count: seriesPoints.EXERCISED.y
      },
      {
        lineColor: seriesPoints.VULNERABLE.series.options.lineColor,
        i18nMessageID: 'detail_applicationCoverage_vulnerableRoutes',
        count: seriesPoints.VULNERABLE.y
      }
    ]

    const titleMsg = intl.formatMessage(
      {
        id: 'detail_applicationCoverage_nRoutes'
      },
      {
        count: seriesPoints.DISCOVERED.y
      }
    )

    let tooltipTpl = getChartTooltipHeaderTpl(
      titleMsg,
      Highcharts.dateFormat('%b %e, %Y %H:%M', timestamp)
    )

    tooltipTpl += '<div style="margin-bottom: 16px"></div>'

    tooltipSeriesRowTplConfig.forEach(({ lineColor, i18nMessageID, count }) => {
      tooltipTpl += getChartTooltipSeriesRowTpl(
        lineColor,
        intl.formatMessage(
          {
            id: i18nMessageID
          },
          {
            routesNumber: count
          }
        )
      )
    })

    return (
      '<div style="' +
      getChartTooltipContainerCss(sharedTooltipPadding) +
      '" >' +
      tooltipTpl +
      '</div>'
    )
  }
}

const getIndividualTooltipRowTpl = (routesNumber: number, label: string) => {
  const routesNumberSymbolCss = `font-weight: ${deprecatedTheme.fontWeights.primary.bold};`
  const routesNumberCss = `margin-right: 2px; font-weight: ${deprecatedTheme.fontWeights.primary.bold};`
  const labelCss = `color: ${theme.color.neutral10}; margin-left: 2px;`

  const routesSymbol = routesNumber >= 0 ? '+' : '-'

  return (
    '<div>' +
    '<span style="' +
    routesNumberSymbolCss +
    '">' +
    routesSymbol +
    '</span>' +
    '<span style="' +
    routesNumberCss +
    '">' +
    Math.abs(routesNumber) +
    '</span>' +
    '<span style="' +
    labelCss +
    '">' +
    label +
    '</span>' +
    '</div>'
  )
}
